import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { trackConsentChanged } from './tracker';

/**
 * Souhlas s cookies / trackingem.
 *
 * Tři kategorie: essential (vždy, nejde vypnout – košík, session, jazyk),
 * analytics (= CDP behaviorální tracking) a marketing (budoucí personalizace;
 * Fáze 1 ho jen sbírá a ukládá). Volba žije v localStorage; nerozhodnutý
 * stav se chová jako odmítnutí – bez souhlasu žádný event neodejde.
 */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = 'zooshopik.consent.v1';

export interface ConsentState {
  readonly analytics: boolean;
  readonly marketing: boolean;
}

interface StoredConsent extends ConsentState {
  readonly version: number;
  readonly decidedAt: string;
}

/**
 * Čistá funkce pro ne-React kód (tracker, cart.tsx). Vrací uloženou volbu,
 * nebo obojí false, dokud zákazník nerozhodl (či po bumpu verze).
 */
export function readConsent(): ConsentState {
  const stored = readStored();
  if (stored === null) return { analytics: false, marketing: false };
  return { analytics: stored.analytics, marketing: stored.marketing };
}

function readStored(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as StoredConsent).version === CONSENT_VERSION &&
      typeof (parsed as StoredConsent).analytics === 'boolean' &&
      typeof (parsed as StoredConsent).marketing === 'boolean'
    ) {
      return parsed as StoredConsent;
    }
    // Stará verze nebo rozbitý tvar – bereme jako nerozhodnuto (re-prompt).
    return null;
  } catch {
    // Privátní režim – volba se neudrží, lišta se ukáže znovu.
    return null;
  }
}

interface ConsentContextValue {
  readonly consent: ConsentState;
  /** Zákazník už rozhodl (platný záznam aktuální verze existuje). */
  readonly decided: boolean;
  /** Lišta je otevřená – buď poprvé, nebo z odkazu „Nastavení cookies". */
  readonly bannerOpen: boolean;
  save(next: ConsentState): void;
  openBanner(): void;
  closeBanner(): void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredConsent | null>(readStored);
  // Bez platného záznamu je lišta otevřená hned; web zůstává plně použitelný.
  const [bannerOpen, setBannerOpen] = useState(() => readStored() === null);

  const save = useCallback((next: ConsentState) => {
    const record: StoredConsent = {
      version: CONSENT_VERSION,
      analytics: next.analytics,
      marketing: next.marketing,
      decidedAt: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Privátní režim – volba platí aspoň pro tuhle stránku (stav v Reactu).
    }
    setStored(record);
    setBannerOpen(false);
    // Účtenka souhlasu – odesílá se i při odmítnutí (jediná výjimka gate).
    trackConsentChanged(next);
  }, []);

  const openBanner = useCallback(() => setBannerOpen(true), []);
  const closeBanner = useCallback(() => setBannerOpen(false), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent:
        stored === null
          ? { analytics: false, marketing: false }
          : { analytics: stored.analytics, marketing: stored.marketing },
      decided: stored !== null,
      bannerOpen,
      save,
      openBanner,
      closeBanner,
    }),
    [stored, bannerOpen, save, openBanner, closeBanner],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (context === null) throw new Error('useConsent musí být uvnitř <ConsentProvider>.');
  return context;
}
